import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { PageProps } from '../../../../utils/constants/data';
import { URLS } from '../../../../utils/constants/urls';
import ListPage from '../../CommonPage';
import { buildCicAndFilter, HeaderFilterTextField } from '../../CommonPage/headerFilterField';
import { pageNo, rowCount, setPageNo } from 'utils/redux/reducer/filter-slice';
import axiosInstance from '../../../../utils/axios';
import { ToolTip } from '../../../../components/Tooltip/ToolTip';

const IDS_LOG_TARGET = 'IDS';

const EMPTY_FILTERS = {
  bookingLineItemId: '',
  echoToken: '',
  responseStatus: '',
};

const FILTER_CHIP_LABELS: Record<keyof typeof EMPTY_FILTERS, string> = {
  bookingLineItemId: 'Line item',
  echoToken: 'Echo token',
  responseStatus: 'Status',
};

const truncate = (text: string | null | undefined, max = 80) => {
  if (text == null || text === '') return '—';
  return text.length <= max ? text : `${text.slice(0, max)}…`;
};

const parseHttpStatus = (val: unknown): number | null => {
  if (val == null || val === '') {
    return null;
  }
  const n = typeof val === 'number' ? val : parseInt(String(val), 10);
  return Number.isFinite(n) ? n : null;
};

const formatBodyForDisplay = (raw: string | null | undefined): string => {
  if (raw == null || raw.trim() === '') {
    return '(empty)';
  }
  const trimmed = raw.trim();
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
};

const IdsLogsList: FC = () => {
  const dispatch = useDispatch();
  const index = useSelector(pageNo);
  const pageSize = useSelector(rowCount);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [bodyDialog, setBodyDialog] = useState<{ open: boolean; title: string; text: string }>({
    open: false,
    title: '',
    text: '',
  });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedFilters(prev =>
        Object.keys(filters).every(key => prev[key as keyof typeof EMPTY_FILTERS] === filters[key as keyof typeof EMPTY_FILTERS])
          ? prev
          : filters,
      );
      dispatch(setPageNo(0));
    }, 350);
    return () => window.clearTimeout(handle);
  }, [dispatch, filters]);

  const updateFilter = (field: keyof typeof EMPTY_FILTERS, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const openBodyDialog = (title: string, raw: string | null | undefined) => {
    setBodyDialog({ open: true, title, text: formatBodyForDisplay(raw) });
  };

  const closeBodyDialog = () => {
    setBodyDialog(prev => ({ ...prev, open: false }));
  };

  const copyBodyText = () => {
    if (bodyDialog.text && bodyDialog.text !== '(empty)') {
      void navigator.clipboard.writeText(bodyDialog.text);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: 'IDS Logs',
      subtitle: 'Review IDS request and response traffic for this property.',
      entityLabel: 'Logs',
      hideActionText: true,
      headerFilters: () => (
        <>
          <HeaderFilterTextField
            label="Booking line item"
            placeholder="Line item ID"
            value={filters.bookingLineItemId}
            onChange={value => updateFilter('bookingLineItemId', value)}
          />
          <HeaderFilterTextField
            label="Echo token"
            placeholder="Exact match"
            value={filters.echoToken}
            onChange={value => updateFilter('echoToken', value)}
          />
          <HeaderFilterTextField
            label="Status"
            placeholder="e.g. 200"
            value={filters.responseStatus}
            onChange={value => updateFilter('responseStatus', value)}
          />
        </>
      ),
      filterChips: (Object.keys(FILTER_CHIP_LABELS) as (keyof typeof EMPTY_FILTERS)[])
        .filter(key => appliedFilters[key].trim())
        .map(key => ({
          key,
          label: `${FILTER_CHIP_LABELS[key]} : ${appliedFilters[key].trim()}`,
        })),
      onClearFilterChip: (key: string) => {
        if (key in EMPTY_FILTERS) {
          const next = { ...filters, [key]: '' };
          setFilters(next);
          setAppliedFilters(next);
        }
      },
      onClearAllFilterChips: () => {
        const next = { ...EMPTY_FILTERS };
        setFilters(next);
        setAppliedFilters(next);
      },
      tableColumn: [
        {
          field: 'bookingLineItemId',
          headerName: 'Booking line item',
          flex: 0.65,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                {val != null && val !== '' ? val : '—'}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'hotelCode',
          headerName: 'Hotel code',
          flex: 0.7,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                {val != null && val !== '' ? val : '—'}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'echoToken',
          headerName: 'Echo token',
          flex: 0.85,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 140, display: 'block' }}>
                {truncate(val, 28)}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'method',
          headerName: 'Method',
          flex: 0.45,
          sortable: false,
        },
        {
          field: 'url',
          headerName: 'URL',
          flex: 2,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" sx={{ maxWidth: 360, display: 'block' }}>
                {truncate(val, 100)}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'responseStatus',
          headerName: 'Status',
          flex: 0.55,
          sortable: false,
          renderCell: (val: unknown) => {
            const code = parseHttpStatus(val);
            const label = val != null && val !== '' ? String(val) : '—';
            if (code == null) {
              const isEmpty = label === '—';
              return (
                <Chip
                  size="small"
                  label={label}
                  variant="outlined"
                  color={isEmpty ? 'default' : 'error'}
                  sx={{ fontWeight: 600 }}
                />
              );
            }
            const ok = code >= 200 && code < 300;
            return <Chip size="small" label={label} color={ok ? 'success' : 'error'} sx={{ fontWeight: 600 }} />;
          },
        },
        {
          field: 'action',
          headerName: 'Action',
          flex: 0.65,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 140, display: 'block' }}>
                {val != null && val !== '' ? val : '—'}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'payloadActions',
          headerName: 'Bodies',
          flex: 1,
          sortable: false,
          renderCell: (_: unknown, row: Record<string, unknown>) => {
            const hasReq = row.requestBody != null && String(row.requestBody).trim() !== '';
            const hasRes = row.responseBody != null && String(row.responseBody).trim() !== '';
            return (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap onClick={e => e.stopPropagation()}>
                <ToolTip title={hasReq ? 'View request body' : 'No request body'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasReq}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0.25, fontSize: '0.75rem' }}
                      onClick={() => openBodyDialog('Request body', row.requestBody as string)}>
                      Request
                    </Button>
                  </span>
                </ToolTip>
                <ToolTip title={hasRes ? 'View response body' : 'No response body'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasRes}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0.25, fontSize: '0.75rem' }}
                      onClick={() => openBodyDialog('Response body', row.responseBody as string)}>
                      Response
                    </Button>
                  </span>
                </ToolTip>
              </Stack>
            );
          },
        },
      ],
    }),
    [appliedFilters, filters],
  );

  const getList = useCallback(
    async (filterData: PageProps) => {
      const body = filterData || {
        page: index + 1,
        size: pageSize,
        sort: ['createdAt:desc'],
      };
      try {
        setLoading(true);
        const { data } = await axiosInstance.post(URLS.fetchMediatorLogs(IDS_LOG_TARGET), {
          ...body,
          filter: buildCicAndFilter(appliedFilters),
        });
        const rows = (data?.content || []).map((item: any, i: number) => ({
          ...item,
          id: [item.traceId, item.bookingLineItemId, i].filter(v => v != null && v !== '').join('-') || i,
        }));
        setPageResponse({ ...data, content: rows });
      } catch (error: any) {
        setPageResponse(error);
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, index, pageSize],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  return (
    <Box>
      <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} isWhiteSpace={false} />
      <Dialog open={bodyDialog.open} onClose={closeBodyDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {bodyDialog.title}
          <IconButton
            aria-label="Copy to clipboard"
            onClick={copyBodyText}
            disabled={!bodyDialog.text || bodyDialog.text === '(empty)'}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: '70vh',
            bgcolor: 'grey.50',
          }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {bodyDialog.text}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBodyDialog} variant="contained" sx={{ textTransform: 'capitalize' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IdsLogsList;
